/**
 * 相册里的一张照片（/album/:photoId，P3：c84a 多级页面单 L3「一张照片内容占主位；单一返回、必要动作、按需展开来源」）。全屏，自带 WorldGate。
 * - 读 pets.albumPhoto（GET /pets/{pet_id}/album/{photo_id}，纯读）：照片＋关联的经历（这趟旅途、那次到店）。
 * - 左上返回到相册：站内有上一页时退回上一页（相册翻过的页还在缓存里，滚动位置由外壳的滚动恢复接上），直接打开时去 /album。
 * - 内容：大图（视频可以播）、TA 配的话、拍在哪、哪天、当地时间（拍摄地时区；缺就写「当地时间待核」）、关联的旅途手账 / 到店。
 *   想象出来的那一类明说「不算 TA 真的去过」。
 * - 找不到（不在这只宠物的相册里、换了宠物、看不到）：说一句、给回相册的路；相册没开放（含演示模式）：温和说明；其余错误可重试。
 *   看得见的字里不写 AI、模型、生成。
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router";
import type { PetPhotoDetail } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { serverNow } from "@/shared/time/clock";
import { Button, ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { WorldGate } from "@/features/world_map/WorldGate";
import { useCurrentPet, type CurrentPet } from "./currentPet";
import { albumPhotoKey, dayText, imagined, localTimeText, placeLine } from "./album";
import "./album.css";
import { AlbumHide } from "./AlbumHide";

type OpenedPhoto = { photoId: string; petId: string; userId: string | null };

export function AlbumPhotoPage() {
  // This route lifetime survives the gate/provider pending subtree. No global or storage identity.
  const opened = useRef<OpenedPhoto | null>(null);
  return (
    <WorldGate>
      <PhotoBody opened={opened} />
    </WorldGate>
  );
}

function PhotoBody({ opened }: { opened: RefObject<OpenedPhoto | null> }) {
  const { photoId = "" } = useParams();
  const current = useCurrentPet();
  const pet = current.status === "ready" ? current.pet : null;
  // Check confirmed identity before accepting a different photo ID. A new account cannot rebind an old route.
  if (pet && current.status === "ready") {
    if (opened.current && (opened.current.petId !== pet.petId || opened.current.userId !== current.userId)) {
      return <Navigate to="/album" replace state={null} />;
    }
    if (!opened.current || opened.current.photoId !== photoId) {
      opened.current = { photoId, petId: pet.petId, userId: current.userId };
    }
  }
  return (
    <Page bare className="ps-album ps-album-photo">
      <TopBar title={pet ? `${pet.name}的照片` : "照片"} back="/album" />
      {current.status === "pending" ? (
        <LoadingState lines={3} label="正在找到 TA…" />
      ) : current.status === "error" ? (
        <ErrorState error={current.error} onRetry={current.retry} />
      ) : pet ? (
        <PhotoDetail key={JSON.stringify([current.userId, pet.petId, photoId])} pet={pet} userId={current.userId} photoId={photoId} />
      ) : (
        <p className="ps-album-quiet" role="status">
          还没有住进来的伙伴。
        </p>
      )}
    </Page>
  );
}

type DetailOwner = {
  userId: string | null; petId: string; photoId: string; mode: string;
  pets: ReturnType<typeof useServices>["pets"]; client: QueryClient; epoch: number; settled: boolean;
};
let nextDetailEpoch = 0;
class DetailScopeEnded extends Error { constructor() { super("Photo detail context ended"); } }
class DetailBindingMismatch extends Error { constructor() { super("Photo detail does not match this route"); } }
function newDetailOwner(userId: string | null, petId: string, photoId: string, mode: string, pets: DetailOwner["pets"], client: QueryClient): DetailOwner {
  return { userId, petId, photoId, mode, pets, client, epoch: ++nextDetailEpoch, settled: false };
}

function PhotoDetail({ pet, userId, photoId }: { pet: CurrentPet; userId: string | null; photoId: string }) {
  const { pets } = useServices();
  const client = useQueryClient();
  const [scope, setScope] = useState(() => newDetailOwner(userId, pet.petId, photoId, env.dataMode, pets, client));
  const committedScope = useRef<DetailOwner | null>(null);
  useLayoutEffect(() => {
    committedScope.current = scope;
    return () => { if (committedScope.current === scope) committedScope.current = null; };
  }, [scope]);
  if (scope.userId !== userId || scope.petId !== pet.petId || scope.photoId !== photoId || scope.mode !== env.dataMode || scope.pets !== pets || scope.client !== client) {
    setScope(newDetailOwner(userId, pet.petId, photoId, env.dataMode, pets, client));
    return null;
  }
  if (env.dataMode === "live" && !userId?.trim()) return <PhotoFailure error={new DetailBindingMismatch()} onRetry={() => undefined} />;
  return <OwnedPhotoDetail key={scope.epoch} scope={scope} pet={pet} userId={userId} photoId={photoId} isCurrent={() => committedScope.current === scope} />;
}

function OwnedPhotoDetail({ scope, pet, userId, photoId, isCurrent }: {
  scope: DetailOwner; pet: CurrentPet; userId: string | null; photoId: string; isCurrent: () => boolean;
}) {
  const { pets } = scope;
  const mount = useRef({ active: false, generation: 0 });
  useLayoutEffect(() => {
    const lifetime = mount.current;
    lifetime.active = true;
    lifetime.generation++;
    return () => { lifetime.active = false; lifetime.generation++; };
  }, [scope]);
  const owns = (generation = mount.current.generation) => mount.current.active && mount.current.generation === generation && isCurrent();
  const detail = useQuery({
    // Keep the exact cache key: manual snapshots and AlbumHide remove this
    // precise key. Each new committed owner must first complete its own read;
    // former fresh cache is never permission to display or skip that read.
    queryKey: albumPhotoKey(userId ?? "-", pet.petId, photoId),
    queryFn: async ({ signal }) => {
      const generation = mount.current.generation;
      if (!owns(generation)) throw new DetailScopeEnded();
      try {
        const result = await pets.albumPhoto(pet.petId, photoId, signal);
        if (!owns(generation)) throw new DetailScopeEnded();
        if (result.photo.pet_id !== pet.petId || result.photo.photo_id !== photoId) throw new DetailBindingMismatch();
        scope.settled = true;
        return result;
      } catch (error) {
        if (!owns(generation)) throw new DetailScopeEnded();
        scope.settled = true;
        throw error;
      }
    },
    enabled: Boolean(photoId) && (scope.mode !== "live" || Boolean(userId?.trim())),
    staleTime: 60_000,
    refetchOnMount: "always",
    retry: (count, error) => {
      if (!owns() || error instanceof DetailScopeEnded || error instanceof DetailBindingMismatch) return false;
      const retry = scope.client.getDefaultOptions().queries?.retry;
      return typeof retry === "function" ? retry(count, error) : retry === true || count < (typeof retry === "number" ? retry : retry === false ? 0 : 3);
    },
  });
  const photo = detail.data?.photo;
  const resource = JSON.stringify([userId, pet.petId, photoId, photo?.pet_id, photo?.photo_id, photo?.generation.state, photo?.asset.kind, photo?.asset.media_url]);
  // Each resource transition gets a new owner, including A→B→A. A failed GET
  // keeps the same resource owner so cached same-URL recovery cannot loop downloads.
  const owner = useMemo(() => Symbol(resource), [resource]);
  const committed = useRef<symbol | null>(null);
  const reading = useRef<symbol | null>(null);
  const [failed, setFailed] = useState<symbol | null>(null);
  // 玩家亲手点「重新读取照片信息」：清掉这张的失败、换一个新节点把同一张图再取一次（I 10-13 方案 2，R1）；不点不重取
  const [attempt, setAttempt] = useState(0);
  useLayoutEffect(() => {
    committed.current = detail.isError || detail.isPending || !scope.settled || !owns() ? null : owner;
    return () => { committed.current = null; };
  }, [owner, detail.isError, detail.isPending, scope.settled, detail.isFetching]);
  const readInformation = async () => {
    if (!owns() || committed.current !== owner || reading.current || detail.isFetching) return;
    reading.current = owner;
    try { await detail.refetch({ cancelRefetch: false }); }
    finally { if (reading.current === owner) reading.current = null; }
  };
  if (!scope.settled || detail.isPending) return <LoadingState lines={3} label="正在翻到这一张…" />;
  if (detail.isError) return <PhotoFailure error={detail.error} onRetry={() => void detail.refetch()} />;
  if (!photo || photo.pet_id !== pet.petId || photo.photo_id !== photoId) return <PhotoFailure error={new DetailBindingMismatch()} onRetry={() => undefined} />;
  return <>
    <PhotoView data={detail.data} resource={resource} attempt={attempt} mediaFailed={failed === owner} reading={detail.isFetching}
      onMediaError={() => { if (committed.current === owner) setFailed(owner); }}
      onRead={() => { if (committed.current === owner && failed === owner) { setFailed(null); setAttempt((n) => n + 1); } return readInformation(); }} />
    {/* 从相册移除（WIRE-01 A7）：照片详情页底部的透明按钮＋轻确认 */}
    <AlbumHide petId={pet.petId} userId={userId} photoId={photoId} />
  </>;
}

function PhotoView({ data, resource, attempt, mediaFailed, reading, onMediaError, onRead }: {
  data: PetPhotoDetail; resource: string; attempt: number; mediaFailed: boolean; reading: boolean;
  onMediaError: () => void; onRead: () => Promise<unknown>;
}) {
  const { photo, experience } = data;
  // 点过「重新读取照片信息」、读完了地址还是这一个（UI2-ALBUM-ERR-01）：照实说还没读到、过一会儿再来，不让玩家以为没点上；换了地址就重新算
  const [phase, setPhase] = useState<{ resource: string; read: boolean } | null>(null);
  const alive = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const still = phase?.resource === resource && phase.read && !reading;
  // 重取以后又没读到：焦点回到新的「重新读取照片信息」按钮（原来那个随失败说明一起撤掉了）
  const failureBox = useRef<HTMLDivElement>(null);
  useEffect(() => { if (mediaFailed && attempt > 0 && phase?.resource === resource) failureBox.current?.querySelector("button")?.focus(); }, [mediaFailed, attempt, phase?.resource, resource]);
  const readAgain = () => {
    setPhase({ resource, read: false });
    void onRead().catch(() => undefined).finally(() => { if (alive.current) setPhase((now) => (now?.resource === resource ? { resource, read: true } : now)); });
  };
  const video = photo.asset.kind === "video";
  const place = placeLine(photo);
  const thisYear = new Date(serverNow()).getFullYear();
  const url = photo.generation.state === "ready" ? photo.asset.media_url : null;
  const journeyId = experience?.journey_id ?? photo.activity?.journey_id ?? null;
  const visitId = experience?.visit_id ?? photo.activity?.visit_id ?? null;
  return (
    <article className="ps-album-detail" data-testid="album-photo">
      <figure className="ps-album-detail__figure">
        {!url ? (
          <div className="ps-album-detail__missing" role="status">
            这一张暂时没有可查看的图片。<Link to="/album">回到相册</Link>
          </div>
        ) : mediaFailed ? (
          <div ref={failureBox} className="ps-album-detail__missing" role="status" aria-live="polite">
            <p>{still
              ? video ? "这段视频还没读到，过一会儿再来看看，照片信息仍在。" : "这张照片还没读到，过一会儿再来看看，照片信息仍在。"
              : video ? "这段视频暂时无法播放，照片信息仍在。" : "这张照片暂时无法显示，照片信息仍在。"}</p>
            <Button block icon="refresh" loading={reading} onClick={readAgain}>重新读取照片信息</Button>
            <p><Link className="ps-btn ps-btn--ghost" to="/album">回到相册</Link></p>
          </div>
        ) : photo.asset.kind === "video" ? (
          <video key={`${resource}#${attempt}`} src={url} controls playsInline preload="metadata" onError={event => { if (event.currentTarget.isConnected) onMediaError(); }} />
        ) : (
          <img key={`${resource}#${attempt}`} src={url} alt={photo.caption ? `照片：${photo.caption}` : "照片"} onError={event => { if (event.currentTarget.isConnected) onMediaError(); }} />
        )}
        {photo.caption ? <figcaption>{photo.caption}</figcaption> : null}
      </figure>
      {imagined(photo) ? (
        <p className="ps-album-detail__imagined">
          <Icon name="sparkle" size={16} />
          想象出来的冒险，不算 TA 真的去过
        </p>
      ) : null}
      <dl className="ps-album-detail__facts">
        {place ? (
          <div>
            <dt>拍在哪</dt>
            <dd>{place}</dd>
          </div>
        ) : null}
        <div>
          <dt>哪天</dt>
          <dd>{dayText(photo.album_day, thisYear)}</dd>
        </div>
        <div>
          <dt>几点</dt>
          <dd>{localTimeText(photo)}</dd>
        </div>
      </dl>
      {journeyId || visitId ? (
        <nav className="ps-album-detail__links" aria-label="这张照片关联的经历">
          {journeyId ? (
            <Link to={`/guides?journey=${encodeURIComponent(journeyId)}`}>
              <Icon name="journey" size={18} />
              <span>{experience?.journey_title ? `这趟旅途：${experience.journey_title}` : "这趟旅途的手账"}</span>
              <Icon name="chevron" size={16} />
            </Link>
          ) : null}
          {visitId ? (
            <Link to={`/visits/${encodeURIComponent(visitId)}`}>
              <Icon name="cup" size={18} />
              <span>{experience?.place_name ? `那次去了${experience.place_name}` : "那一次到店"}</span>
              <Icon name="chevron" size={16} />
            </Link>
          ) : null}
        </nav>
      ) : null}
      {/* 来自对话的照片能回到原对话（PHOTO-ASK-FLOW-01；以后端给的引用为准：refs.message_ids 非空才给） */}
      {(photo.refs?.message_ids?.length ?? 0) > 0 ? (
        <p className="ps-album-detail__chat">
          <Link className="ps-btn ps-btn--ghost" to="/communicator">回到和 TA 的对话</Link>
        </p>
      ) : null}
    </article>
  );
}

function PhotoFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof DetailBindingMismatch || (isApiError(error) && error.isNotFound)) {
    return (
      <div className="ps-album-note" role="status">
        <span className="ps-album-note__icon" aria-hidden="true">
          <Icon name="album" size={22} />
        </span>
        <h2>这一张找不到了</h2>
        <p>可能不在这只伙伴的相册里，或者已经从相册里拿掉了。</p>
        <Link className="ps-btn ps-btn--ghost" to="/album">
          回到相册
        </Link>
      </div>
    );
  }
  if (isApiError(error) && error.isCapabilityUnavailable) {
    return (
      <div className="ps-album-note" role="note">
        <span className="ps-album-note__icon" aria-hidden="true">
          <Icon name="album" size={22} />
        </span>
        <h2>{env.dataMode === "fixture" ? "演示模式没有相册照片" : "相册暂时打不开"}</h2>
        <p>{env.dataMode === "fixture" ? "等你的伙伴住进来，TA 带回来的照片会收在相册里。" : "过一会儿再来看看。"}</p>
        <Link className="ps-btn ps-btn--ghost" to="/album">
          回到相册
        </Link>
      </div>
    );
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}
